/**
 * New starters (2 Oct): every new colleague the reader is involved with, from
 * HR's New User Request to their business-system access, on one page.
 *
 * Reached from the header's "New starters" entry, which only appears for
 * someone who has a case (`/auth/me` → `has_starter_cases`). Cards with an
 * "Action for you" badge come with an accent edge: it is somebody's normal
 * next step, not an alarm.
 */

import { Icon, UserPlus } from "@norican/icons";
import { Button, Typography } from "@norican/react";
import { Link } from "react-router";

import {
  EmptyStateBlock,
  ErrorState,
  LoadingRows,
  PageContainer,
  ResultStateNotice,
} from "@/components/common";
import { useDocumentTitle } from "@/hooks";
import { useT } from "@/i18n";
import { useMe } from "@/lib/api";

import { useStarterCases } from "./api";
import { StarterCard } from "./components/starter-card";

export function StartersPage() {
  const { t } = useT("tickets");
  useDocumentTitle(t("starters.title"));
  const query = useStarterCases();
  const items = query.data?.items ?? [];
  const actionCount = query.data?.action_count ?? 0;
  const { data: me } = useMe();
  // The user access front door (5 Oct): the same new hire's access, asked once.
  const access =
    me?.access_front_door === true ? (
      <Button variant="outline" tone="neutral" render={<Link to="/access/new" />}>
        {t("access.wizard.title")}
      </Button>
    ) : undefined;

  return (
    <PageContainer title={t("starters.title")} subtitle={t("starters.subtitle")} actions={access}>
      <div className="flex flex-col gap-4">
        <ResultStateNotice result={query.data?.result} />
        {actionCount > 0 ? (
          <Typography as="p" variant="body-sm" className="text-fg font-medium">
            {t("starters.needAction", { count: actionCount })}
          </Typography>
        ) : null}
        {query.isPending ? (
          <LoadingRows rows={3} />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : items.length === 0 ? (
          <EmptyStateBlock
            icon={<Icon icon={UserPlus} size="lg" tone="accent" />}
            title={t("starters.empty.title")}
            description={t("starters.empty.description")}
          />
        ) : (
          <ul className="flex list-none flex-col gap-3 p-0" aria-label={t("starters.listLabel")}>
            {items.map((item) => (
              <li key={item.case_id}>
                <StarterCard item={item} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </PageContainer>
  );
}
